import React, { useRef } from 'react';
import { Download, X, Calendar, MapPin, Users, Mail, Phone, CheckCircle, Award } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ConfirmationLetter = ({ letterData, onClose }) => {
  const letterRef = useRef();

  // Get event config with fallbacks
  const eventConfig = letterData.event_config || {
    platform_name: 'EvalentX',
    edition: 2,
    venue: 'IIT Gandhinagar',
    start_date: '11 April 2026',
    end_date: '12 April 2026'
  };

  const downloadPDF = async () => {
    const element = letterRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 0;

    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`${eventConfig.platform_name}_Confirmation_${letterData.participant_name.replace(/\s+/g, '_')}.pdf`);
  };

  // Sort team members to show the current participant first
  const sortedMembers = [...(letterData.team_members || [])];
  const currentMemberIndex = sortedMembers.findIndex(
    m => m.name === letterData.participant_name || m.email === letterData.participant_email
  );
  if (currentMemberIndex > 0) {
    const currentMember = sortedMembers.splice(currentMemberIndex, 1)[0];
    sortedMembers.unshift(currentMember);
  }

  // Format issue date
  const issueDate = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-4xl w-full my-4 shadow-2xl max-h-[95vh] overflow-y-auto">
        {/* Modal Header - Action Bar */}
        <div className="sticky top-0 z-10 bg-slate-900 text-white p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Award size={24} className="text-amber-400" />
            <div>
              <h2 className="text-lg font-bold">Confirmation Letter</h2>
              <p className="text-slate-400 text-xs">Official Registration Document</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={downloadPDF}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <Download size={18} />
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="bg-slate-700 hover:bg-slate-600 p-2 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Letter Content - PDF Ready */}
        <div ref={letterRef} className="bg-white" style={{ minHeight: '1100px' }}>
          {/* Professional Header */}
          <div className="border-b-4 border-slate-800">
            {/* Top Bar */}
            <div className="bg-slate-800 text-white py-3 px-8">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                  <span className="text-xs font-medium tracking-widest uppercase">Official Document</span>
                </div>
                <span className="text-xs text-slate-400">Ref: {letterData.participant_id}</span>
              </div>
            </div>
            
            {/* Logo & Title Section */}
            <div className="bg-white px-8 py-6">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-4xl font-black text-slate-900 tracking-tight">
                    {eventConfig.platform_name.toUpperCase()}
                  </h1>
                  <p className="text-slate-500 font-medium mt-1">
                    {eventConfig.edition === 1 ? '1st' : eventConfig.edition === 2 ? '2nd' : `${eventConfig.edition}th`} Edition • {eventConfig.venue}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Issue Date</p>
                  <p className="text-sm font-semibold text-slate-700">{issueDate}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Event Details Banner */}
          <div className="bg-slate-50 border-b border-slate-200 px-8 py-4">
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-slate-600" />
                <span className="text-sm font-medium text-slate-700">
                  {eventConfig.start_date} – {eventConfig.end_date}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-slate-600" />
                <span className="text-sm font-medium text-slate-700">{eventConfig.venue}, Gujarat, India</span>
              </div>
            </div>
          </div>

          {/* Main Letter Content */}
          <div className="px-8 py-8">
            {/* Letter Title */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 tracking-wide uppercase">
                Registration Confirmation
              </h2>
              <div className="w-24 h-1 bg-amber-400 mx-auto mt-2"></div>
            </div>

            {/* Registration Details Card */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Registration Number</p>
                  <p className="text-lg font-bold text-slate-800 font-mono">HS-IITGN-{letterData.registration_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Payment Status</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                    letterData.payment_status === 'completed' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    <CheckCircle size={14} />
                    {letterData.payment_status === 'completed' ? 'PAID' : 'PENDING'}
                  </span>
                </div>
              </div>
            </div>

            {/* Recipient Section */}
            <div className="mb-6">
              <p className="text-sm text-slate-500 mb-1">To,</p>
              <h3 className="text-xl font-bold text-slate-900">{letterData.participant_name}</h3>
              <p className="text-slate-600">
                Team: <span className="font-semibold text-slate-800">{letterData.team_name}</span>
              </p>
            </div>

            {/* Subject */}
            <div className="border-l-4 border-amber-400 bg-amber-50 pl-4 py-3 mb-6">
              <p className="font-semibold text-slate-800">
                Subject: Congratulations on Your Registration – {eventConfig.platform_name} {eventConfig.venue} Edition
              </p>
            </div>

            {/* Letter Body */}
            <div className="space-y-4 text-slate-700 leading-relaxed text-[15px]">
              <p>Dear <span className="font-semibold">{letterData.participant_name}</span>,</p>

              <p>
                Congratulations on your successful registration for the <strong>{eventConfig.platform_name} {eventConfig.venue} Edition</strong>, 
                scheduled for <strong>{eventConfig.start_date} – {eventConfig.end_date}</strong>. We are delighted to welcome you as 
                a participant and a valued member of Team <strong>{letterData.team_name}</strong>.
              </p>

              <p>
                Your decision to join {eventConfig.platform_name} reflects your dedication to innovation, technology, and continuous learning. 
                We appreciate your enthusiasm and are proud to have you represent your team in this highly competitive national-level event 
                hosted at {eventConfig.venue}.
              </p>

              {/* Eligibility Box */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 my-6">
                <p className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Award className="text-amber-500" size={18} />
                  As a confirmed participant, you are eligible to attend:
                </p>
                <ul className="space-y-2 ml-6 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 font-bold mt-1">•</span>
                    <span>Workshops on <strong>Artificial Intelligence</strong>, <strong>Data Science</strong>, and <strong>Ethical Hacking</strong> (available online and offline).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 font-bold mt-1">•</span>
                    <span>The <strong>24-hour Hackathon</strong>, beginning on the event start date at 12:30 PM.</span>
                  </li>
                </ul>
              </div>

              {/* Team Members */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 my-6">
                <p className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Users className="text-slate-600" size={18} />
                  Your Team Members:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {sortedMembers.map((member, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center gap-2 p-2 rounded ${
                        index === 0 
                          ? 'bg-amber-100 border border-amber-300' 
                          : 'bg-white border border-slate-200'
                      }`}
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {index + 1}
                      </span>
                      <span className={`text-sm ${index === 0 ? 'font-bold text-amber-800' : 'text-slate-700'}`}>
                        {member.name} {index === 0 && <span className="text-amber-600">(You)</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What to Expect */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 my-6">
                <p className="font-bold text-slate-800 mb-3">What to Expect Next:</p>
                <ul className="space-y-2 ml-6 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-slate-500 font-bold mt-1">•</span>
                    <span>Detailed instructions regarding schedules, workshop links, reporting guidelines, and problem statements will be shared soon.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-500 font-bold mt-1">•</span>
                    <span>After event completion, you will receive a <strong>blockchain-verified certificate</strong> and an opportunity to apply for internships with <strong>100+ national and global companies</strong>.</span>
                  </li>
                </ul>
              </div>

              <p>
                We look forward to your active participation and are excited to witness your contributions to the {eventConfig.platform_name} experience. 
                Wishing you and your team the very best for the competition ahead.
              </p>

              <p className="flex items-center gap-2">
                For any queries, please reach out at 
                <a href="mailto:support@evalentx.com" className="text-slate-800 font-semibold underline flex items-center gap-1">
                  <Mail size={14} />
                  support@evalentx.com
                </a>
              </p>
            </div>

            {/* Signature Section */}
            <div className="mt-10 pt-6 border-t-2 border-slate-200">
              <div className="flex justify-between items-end">
                {/* QR Code */}
                <div className="text-center">
                  <div className="bg-white border-2 border-slate-300 p-2 rounded-lg inline-block mb-2">
                    {letterData.qr_code ? (
                      <img 
                        src={`data:image/png;base64,${letterData.qr_code}`} 
                        alt="Verification QR Code" 
                        className="w-24 h-24" 
                      />
                    ) : (
                      <div className="w-24 h-24 flex items-center justify-center bg-slate-100 rounded">
                        <span className="text-slate-400 text-xs">QR Code</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 font-medium">Scan to Verify</p>
                  <p className="text-xs text-slate-700 font-mono">{letterData.participant_id}</p>
                </div>

                {/* Signature */}
                <div className="text-right">
                  <p className="text-slate-500 text-sm mb-2">Warm Regards,</p>
                  <div className="border-b-2 border-slate-400 pb-1 mb-2 inline-block">
                    <p className="text-xl font-bold text-slate-800">Team {eventConfig.platform_name}</p>
                  </div>
                  <p className="text-sm text-slate-600">{eventConfig.venue} Edition</p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Footer */}
          <div className="bg-slate-800 text-white px-8 py-4 mt-8">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-slate-400">
                  This is an official confirmation letter • Document ID: {letterData.participant_id}
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <Mail size={12} />
                  support@evalentx.com
                </span>
                <span className="flex items-center gap-1">
                  <Phone size={12} />
                  +91 7349141410
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationLetter;
